import { useCallback, useEffect, useMemo, useState } from "react";
import { Camera, Copy, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];
const EMPTY_TASK = { title: "", description: "", recipe_link: "", target_par_qty: "", requires_photo_proof: false, assigned_to_user_id: "", station: "", recurrence: "none", recurrence_days: [], recurrence_interval_hours: 2 };

const recLabel = (t) => {
  if (t.recurrence === "daily") return "Daily";
  if (t.recurrence === "weekly") return "Weekly " + (t.recurrence_days || []).map((d) => DAY_LETTERS[d]).join("");
  if (t.recurrence === "interval") return `Every ${t.recurrence_interval_hours}h`;
  return "";
};

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [team, setTeam] = useState([]);
  const [settings, setSettings] = useState({ windows: [] });
  const [tplDialog, setTplDialog] = useState(null); // {id?, name, role, window_id}
  const [taskDialog, setTaskDialog] = useState(null); // {templateId, taskId?, ...fields}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, m, s] = await Promise.all([api.get("/templates"), api.get("/team"), api.get("/settings")]);
      setTemplates(t.data);
      setTeam(m.data);
      setSettings(s.data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load templates"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const assigneeOptions = useMemo(() => {
    const role = templates.find((t) => t.id === taskDialog?.templateId)?.role;
    return team.filter((m) => m.primary_role === role);
  }, [team, templates, taskDialog?.templateId]);

  const saveTemplate = async () => {
    setBusy(true);
    try {
      if (tplDialog.id) {
        await api.put(`/templates/${tplDialog.id}`, { name: tplDialog.name, role: tplDialog.role, window_id: tplDialog.window_id || null });
        toast.success("Template updated");
      } else {
        await api.post("/templates", { name: tplDialog.name, role: tplDialog.role, window_id: tplDialog.window_id || null });
        toast.success("Template created");
      }
      setTplDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not save template"));
    } finally {
      setBusy(false);
    }
  };

  const duplicateTemplate = async (t) => {
    try {
      await api.post(`/templates/${t.id}/duplicate`);
      toast.success(`Duplicated "${t.name}" with all its tasks`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not duplicate template"));
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}" and all its tasks?`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not delete template"));
    }
  };

  const saveTask = async () => {
    setBusy(true);
    const { templateId, taskId, ...fields } = taskDialog;
    fields.assigned_to_user_id = fields.assigned_to_user_id || null;
    if (fields.recurrence !== "weekly") fields.recurrence_days = [];
    try {
      if (taskId) {
        await api.put(`/tasks/${taskId}`, fields);
        toast.success("Task updated");
      } else {
        await api.post(`/templates/${templateId}/tasks`, fields);
        toast.success("Task added");
      }
      setTaskDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not save task"));
    } finally {
      setBusy(false);
    }
  };

  const duplicateTask = async (templateId, task) => {
    try {
      await api.post(`/templates/${templateId}/tasks`, {
        title: `${task.title} (copy)`,
        description: task.description || "",
        recipe_link: task.recipe_link || "",
        target_par_qty: task.target_par_qty || "",
        requires_photo_proof: task.requires_photo_proof || false,
        assigned_to_user_id: task.assigned_to_user_id || null,
        station: task.station || "",
        recurrence: task.recurrence || "none",
        recurrence_days: task.recurrence_days || [],
        recurrence_interval_hours: task.recurrence_interval_hours || 2,
      });
      toast.success(`Duplicated "${task.title}"`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not duplicate task"));
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success("Task deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not delete task"));
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8" data-testid="templates-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]">Shift Templates</h1>
          <p className="text-sm text-slate-500 mt-1">Build the checklists your crew runs every shift.</p>
        </div>
        <button
          onClick={() => setTplDialog({ name: "", role: settings.staff_types?.[0]?.name || "Server", window_id: "" })}
          data-testid="create-template-button"
          className="h-11 px-5 rounded-xl bg-terra-500 text-white font-display font-bold flex items-center gap-2 hover:bg-terra-600 transition-colors"
        >
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="mt-8 p-6 rounded-2xl bg-white border border-[#E4DDD3] text-slate-500 text-sm" data-testid="no-templates-manager">
          No templates yet. Create your first one — e.g. "Friday Night Prep" for Bartenders.
        </div>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          {templates.map((t) => (
            <div key={t.id} className="rounded-2xl bg-white border border-[#E4DDD3] overflow-hidden" data-testid={`manager-template-card-${t.id}`}>
              <div className="p-4 sm:p-6 flex items-start justify-between gap-3 border-b border-[#E4DDD3]">
                <div>
                  <div className="font-display font-semibold text-lg text-[#1A1D20]">{t.name}</div>
                  <span className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full bg-[#FDF0EB] border border-terra-400/40 text-terra-600 uppercase tracking-wider">{t.role}</span>
                  {t.window_id && (
                    <span className="inline-block mt-1.5 ml-1.5 text-xs px-2 py-0.5 rounded-full bg-white border border-[#E4DDD3] text-slate-600" data-testid={`template-window-chip-${t.id}`}>
                      {settings.windows.find((w) => w.id === t.window_id)?.name || "Window"}
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => duplicateTemplate(t)} className="p-2 rounded-lg text-slate-500 hover:text-terra-600 hover:bg-[#FDF0EB] transition-colors" data-testid={`duplicate-template-button-${t.id}`} title="Duplicate template">
                    <Copy className="w-4 h-4" />
                  </button>
                  <button onClick={() => setTplDialog({ id: t.id, name: t.name, role: t.role, window_id: t.window_id || "" })} className="p-2 rounded-lg text-slate-500 hover:text-terra-600 hover:bg-[#FDF0EB] transition-colors" data-testid={`edit-template-button-${t.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteTemplate(t)} className="p-2 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors" data-testid={`delete-template-button-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="divide-y divide-[#E4DDD3]/60">
                {t.tasks.map((task) => (
                  <div key={task.id} className="px-4 sm:px-6 py-3 flex items-center gap-3" data-testid={`manager-task-row-${task.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#1A1D20] flex items-center gap-2 flex-wrap">
                        {task.title}
                        {task.requires_photo_proof && <Camera className="w-3.5 h-3.5 text-terra-600" title="Photo proof required" />}
                        {task.assigned_to_name && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-300/40 text-emerald-700" data-testid={`task-assignee-badge-${task.id}`}>
                            {task.assigned_to_name} only
                          </span>
                        )}
                        {task.station && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#FAF6F0] border border-[#E4DDD3] text-slate-600" data-testid={`task-station-badge-${task.id}`}>{task.station}</span>
                        )}
                        {task.recurrence && task.recurrence !== "none" && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#FDF0EB] border border-terra-400/40 text-terra-600" data-testid={`task-recurrence-badge-${task.id}`}>{recLabel(task)}</span>
                        )}
                      </div>
                      {task.target_par_qty && <div className="text-xs font-mono text-terra-600/70 mt-0.5">PAR {task.target_par_qty}</div>}
                    </div>
                    <button
                      onClick={() => duplicateTask(t.id, task)}
                      className="p-1.5 rounded-lg text-gray-500 hover:text-terra-600 transition-colors"
                      data-testid={`duplicate-task-button-${task.id}`}
                      title="Duplicate task"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setTaskDialog({ templateId: t.id, taskId: task.id, title: task.title, description: task.description, recipe_link: task.recipe_link, target_par_qty: task.target_par_qty, requires_photo_proof: task.requires_photo_proof, assigned_to_user_id: task.assigned_to_user_id || "", station: task.station || "", recurrence: task.recurrence || "none", recurrence_days: task.recurrence_days || [], recurrence_interval_hours: task.recurrence_interval_hours || 2 })}
                      className="p-1.5 rounded-lg text-slate-500 hover:text-terra-600 transition-colors"
                      data-testid={`edit-task-button-${task.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteTask(task.id)} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 transition-colors" data-testid={`delete-task-button-${task.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setTaskDialog({ templateId: t.id, ...EMPTY_TASK })}
                  data-testid={`add-task-button-${t.id}`}
                  className="w-full px-4 sm:px-6 py-3 text-left text-sm text-terra-600 hover:bg-terra-500/5 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add task
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!tplDialog} onOpenChange={() => setTplDialog(null)}>
        <DialogContent className="bg-white border-[#E4DDD3]" data-testid="template-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[#1A1D20] font-display">{tplDialog?.id ? "Edit Template" : "New Template"}</DialogTitle>
          </DialogHeader>
          {tplDialog && (
            <div className="space-y-4">
              <input
                value={tplDialog.name}
                onChange={(e) => setTplDialog({ ...tplDialog, name: e.target.value })}
                placeholder="Add Template Name, e.g., Morning Opening Tasks"
                data-testid="template-name-input"
                className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
              />
              <label className="block text-xs text-slate-500 uppercase tracking-wider" data-testid="template-staff-type-label">Add Staff Types in Settings</label>
              <select
                value={tplDialog.role}
                onChange={(e) => setTplDialog({ ...tplDialog, role: e.target.value })}
                data-testid="template-role-select"
                className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] focus:outline-none focus:border-terra-400/60"
              >
                {(settings.staff_types || []).map((st) => <option key={st.id} value={st.name}>{st.name}</option>)}
              </select>
              <select
                value={tplDialog.window_id}
                onChange={(e) => setTplDialog({ ...tplDialog, window_id: e.target.value })}
                data-testid="template-window-select"
                className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] focus:outline-none focus:border-terra-400/60"
              >
                <option value="">No window (any time)</option>
                {settings.windows.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.start}–{w.end})</option>)}
              </select>
              <button
                onClick={saveTemplate}
                disabled={busy || !tplDialog.name.trim()}
                data-testid="template-save-button"
                className="w-full h-12 rounded-xl bg-terra-500 text-white font-display font-bold hover:bg-terra-600 disabled:opacity-50 transition-colors"
              >
                {busy ? "Saving…" : "Save Template"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!taskDialog} onOpenChange={() => setTaskDialog(null)}>
        <DialogContent className="bg-white border-[#E4DDD3]" data-testid="task-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[#1A1D20] font-display">{taskDialog?.taskId ? "Edit Task" : "New Task"}</DialogTitle>
          </DialogHeader>
          {taskDialog && (
            <div className="space-y-4">
              <input
                autoFocus
                value={taskDialog.title}
                onChange={(e) => setTaskDialog({ ...taskDialog, title: e.target.value })}
                placeholder='Title, e.g. "Prep Salsa" or "Fold Napkins"'
                data-testid="task-title-input"
                className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
              />
              <textarea
                value={taskDialog.description}
                onChange={(e) => setTaskDialog({ ...taskDialog, description: e.target.value })}
                placeholder="Description / Instructions / Steps"
                rows={3}
                data-testid="task-description-input"
                className="w-full px-4 py-3 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
              />
              <input
                value={taskDialog.target_par_qty}
                onChange={(e) => setTaskDialog({ ...taskDialog, target_par_qty: e.target.value })}
                placeholder='(Optional) Target stock level (PAR), e.g."25 Napkins"'
                data-testid="task-par-qty-input"
                className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
              />
              <input
                value={taskDialog.station}
                onChange={(e) => setTaskDialog({ ...taskDialog, station: e.target.value })}
                placeholder='(Optional) Area or Station, e.g. "Bar" or "Patio"'
                data-testid="task-station-input"
                className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
              />
              <div>
                <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1.5">Recurrence</label>
                <select
                  value={taskDialog.recurrence}
                  onChange={(e) => setTaskDialog({ ...taskDialog, recurrence: e.target.value })}
                  data-testid="task-recurrence-select"
                  className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] focus:outline-none focus:border-terra-400/60"
                >
                  <option value="none">None — manual shifts only</option>
                  <option value="daily">Daily (every business day)</option>
                  <option value="weekly">Weekly (pick days)</option>
                  <option value="interval">Interval (every N hours in window)</option>
                </select>
              </div>
              {taskDialog.recurrence === "weekly" && (
                <div className="flex gap-1.5" data-testid="task-recurrence-days">
                  {DAY_LETTERS.map((d, i) => (
                    <button
                      key={`day-${i}`}
                      type="button"
                      onClick={() => setTaskDialog({ ...taskDialog, recurrence_days: taskDialog.recurrence_days.includes(i) ? taskDialog.recurrence_days.filter((x) => x !== i) : [...taskDialog.recurrence_days, i].sort() })}
                      data-testid={`recurrence-day-${i}`}
                      className={`w-9 h-9 rounded-lg border text-xs font-bold transition-colors ${taskDialog.recurrence_days.includes(i) ? "bg-terra-500 text-white border-terra-400" : "bg-[#FAF6F0] border-[#E4DDD3] text-slate-500 hover:border-terra-400/50"}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              )}
              {taskDialog.recurrence === "interval" && (
                <div>
                  <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1.5">Every N hours (within the template's window)</label>
                  <input
                    type="number"
                    min={1}
                    max={12}
                    value={taskDialog.recurrence_interval_hours}
                    onChange={(e) => setTaskDialog({ ...taskDialog, recurrence_interval_hours: parseInt(e.target.value) || 2 })}
                    data-testid="task-recurrence-interval-input"
                    className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] focus:outline-none focus:border-terra-400/60"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1.5">Assign to</label>
                <select
                  value={taskDialog.assigned_to_user_id}
                  onChange={(e) => setTaskDialog({ ...taskDialog, assigned_to_user_id: e.target.value })}
                  data-testid="task-assignee-select"
                  className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] focus:outline-none focus:border-terra-400/60"
                >
                  <option value="">All {templates.find((t) => t.id === taskDialog.templateId)?.role || "staff"}s</option>
                  {assigneeOptions.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} only</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-3 text-sm text-slate-600 cursor-pointer" data-testid="task-photo-toggle"><input
                  type="checkbox"
                  checked={taskDialog.requires_photo_proof}
                  onChange={(e) => setTaskDialog({ ...taskDialog, requires_photo_proof: e.target.checked })}
                  className="w-5 h-5 accent-terra-500"
                  data-testid="task-photo-checkbox"
                />
                Requires photo proof to complete
              </label>
              <button
                onClick={saveTask}
                disabled={busy || !taskDialog.title.trim()}
                data-testid="task-save-button"
                className="w-full h-12 rounded-xl bg-terra-500 text-white font-display font-bold hover:bg-terra-600 disabled:opacity-50 transition-colors"
              >
                {busy ? "Saving…" : "Save Task"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
